import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import injectStyles from 'react-jss'
import classname from 'classnames'
import _ from 'lodash'

import Button from '@material-ui/core/Button'
import { makeStyles } from '@material-ui/core'
import { PATHS } from '../../../../Config/Routing/paths'
import StyledBreadcrumbs from '../../../../Components/StyledBreadcrumbs'
import { PageTitle } from '../../../../Components/PageTitle'
import ArrowBackIcon from '../../icons/ArrowBackIcon'
import { styles } from '../../styles'
import BreadcrumbsSettingsIcon from '../../../../Assets/Images/icons/breadcrumbsSettings'
import Input from '../../../../Components/Inputs/Input'
import Select from '../../../../Components/Inputs/Select'
import LoadingIndicator from '../../../../Components/LoadingIndicator/LoadingIndicator'
import DatePicker from '../../components/DatePicker'
import { useParams } from 'react-router-dom/cjs/react-router-dom'
import IntegrationsService from '../../../../Module/IntegrationsService/client'
import { ToggleButtonUnstyled } from '../../../../Components/Form/ToggleButton/unstyled'

const useStyles = makeStyles(() => ({
  wrap: {
    paddingRight: '2px !important',
  },
  scroll: {
    overflow: 'scroll',
    height: 'calc(100vh - 198px)',
  },
  inputsWrapper: {
    width: 480,
    display: 'flex',
    flexDirection: 'column',
    paddingRight: 14,
  },
  button: { marginBottom: 30 },
  mb20: { marginBottom: 20 },
  mt20: { marginTop: 20 },
  buttonGroup: {
    width: 'max-content',
    height: 54,
    position: 'relative',
    zIndex: 1,
    marginBottom: -60,
    paddingTop: 21,
    paddingLeft: 4,
    '& > button': {
      backgroundColor: '#f9f9ff',
      borderRadius: 8,
      '& .MuiToggleButton-label': {
        width: '10px',
      },
    },
  },
  inputGroup: {
    '& > div > input': {
      paddingLeft: 75,
    },
  },
  inputPrefix: {
    '& > div > input': {
      paddingLeft: 34,
    },
  },
  inputWrapper: {
    position: 'relative',
  },
  prefix: {
    position: 'absolute',
    top: '34px',
    zIndex: 1,
    left: '16px',
    color: '#333333',
    fontWeight: 400,
  },
}))

const getForm = async integrationId => {
  const form = await IntegrationsService.readForm({
    integrationId,
    type: 'SWELL_CREATE_COUPONS',
  })
  return form
}
const getFormValues = fields =>
  Object.entries(fields).reduce((acc, [name, input]) => {
    if (input.inputType === 'form') {
      acc[name] = getFormValues(input.form)
    } else {
      acc[name] = input.value
    }
    return acc
  }, {})
const formatOptions = options =>
  options.map(option => ({
    name: option.label,
    label: option.label,
    value: option.id,
  }))

const CouponForm = ({ classes: sharedStyles }) => {
  const history = useHistory()
  const classes = useStyles()
  const { integrationId } = useParams()
  const [isLoading, setIsLoading] = useState(true)
  const [fields, setFields] = useState({})
  const [renderInputs, setRenderInputs] = useState([])
  const [isClickSubmit, setIsClickSubmit] = useState(false)
  const [isNeedUpdateInputs, setIsNeedUpdateInputs] = useState(false)

  const onBack = () => {
    history.push(
      `${PATHS.main.dashboard}${PATHS.dashboard.clientSettings}${
        PATHS.clientSettings.coupons
      }${PATHS.clientSettings.createCoupon}${PATHS.clientSettings.stores}`,
    )
  }

  const onChange = (type, parentType, isPercent, isDate) => (value, error) => {
    if (isPercent && value > 100) {
      return
    }

    setFields(prev => {
      if (!parentType) {
        const isOnlyNumber = prev[type].inputType === 'number'

        const newValue = isOnlyNumber && isNaN(value) ? prev[type].value : value

        return {
          ...prev,
          [type]: {
            ...prev[type],
            value: newValue,
            error,
          },
        }
      } else {
        const isOnlyNumber = prev[parentType].form[type].inputType === 'number'

        const newValue =
          isOnlyNumber && isNaN(value)
            ? prev[parentType].form[type].value
            : value

        return {
          ...prev,
          [parentType]: {
            ...prev[parentType],
            form: {
              ...prev[parentType].form,
              [type]: {
                ...prev[parentType].form[type],
                value: newValue,
                error,
              },
            },
          },
        }
      }
    })

    if (isDate) {
      setIsNeedUpdateInputs(true)
    }
  }

  const fetchForm = async () => {
    setIsLoading(true)
    const form = await getForm(integrationId)
    setFields(form)
    setIsLoading(false)
  }

  useEffect(() => {
    fetchForm()
  }, [])

  useEffect(() => {
    setRenderInputs(getInputs(fields))

    if (isNeedUpdateInputs) {
      setIsNeedUpdateInputs(false)
    }
  }, [JSON.stringify(fields).length, isNeedUpdateInputs])

  useEffect(() => {
    if (isClickSubmit) {
      const error = checkErrors()
      if (!error) {
        onSubmit()
      }
      setIsClickSubmit(false)
    }
  }, [isClickSubmit])

  const checkErrors = () => {
    let error = false
    const firstKeys = Object.keys(fields)
    const newFields = firstKeys.reduce((acc, firstKey) => {
      if (Object.keys(fields[firstKey]).includes('form')) {
        Object.entries(fields[firstKey]).forEach(([formKey, formInput]) => {
          if (formInput.isRequired) {
            const formInputError =
              fields[firstKey].form[formKey].isRequired &&
              !fields[firstKey].form[formKey].value
                ? 'required'
                : false

            acc[firstKey].form[formKey] = {
              ...fields[firstKey].form[formKey],
              error: formInputError,
            }
            if (formInputError) {
              error = formInputError
            }
          } else {
            acc[firstKey] = {
              ...fields[firstKey],
              form: { ...fields[firstKey].form },
            }
          }
        })
      } else {
        const inputError =
          fields[firstKey].isRequired && !fields[firstKey].value
            ? 'required'
            : false

        acc[firstKey] = {
          ...fields[firstKey],
          error: inputError,
        }
        if (inputError) {
          error = inputError
        }
      }

      return acc
    }, {})
    setFields(newFields)

    return error
  }

  const getValue = (name, inputType, parentField) => {
    if (inputType === 'select') {
      return parentField
        ? fields[parentField].form[name].value || null
        : fields[name].value || null
    }
    if (inputType === 'date') {
      return parentField
        ? fields[parentField].form[name].value || new Date()
        : fields[name].value || new Date()
    }

    return parentField
      ? fields[parentField].form[name].value || ''
      : fields[name].value || ''
  }

  const checkRule = (fieldValue, rule) => {
    if (rule.$eq) {
      return fieldValue === rule.$eq
    }
  }

  const calculateVisibility = rules => {
    if (!rules) return true
    let isVisible = true
    Object.entries(rules).forEach(([path, rule]) => {
      const fieldValue = _.get(fields, `${path}.value`)
      isVisible = isVisible ? checkRule(fieldValue, rule) : isVisible
    })

    return isVisible
  }

  const getInputs = (data, parentField) => {
    return Object.entries(data).reduce((acc, [name, input], index, arr) => {
      const isVisible = calculateVisibility(input.isVisible)
      if (!isVisible) return acc

      if (['string', 'number'].includes(input.inputType)) {
        const isPercent = name.includes('percent')

        let isGroupedWithPrevInput = false
        if (input.inputGroup) {
          const [, prevInput] = arr[index - 1]

          isGroupedWithPrevInput =
            input.inputGroup && input.inputGroup === prevInput.inputGroup
        }

        acc.push(
          <div className={classes.inputWrapper}>
            {input.inputPrefix ? (
              <span className={classes.prefix}>{input.inputPrefix}</span>
            ) : null}

            <Input
              label={`${input.label} ${
                input.isRequired ? '(Required)' : '(Optional)'
              }`}
              placeholder={'Type here'}
              value={getValue(name, input.inputType, parentField)}
              onChange={onChange(name, parentField, isPercent)}
              description={input.description}
              required={input.isRequired}
              additionalError={input.error}
              isNewError={true}
              className={classname(null, {
                [classes.inputGroup]: isGroupedWithPrevInput,
                [classes.inputPrefix]: input.inputPrefix,
              })}
              cypress_id={input.label.toLowerCase().replace(/\s+/g, '_')}
            />
          </div>,
        )
      }

      if (['select'].includes(input.inputType)) {
        const options = formatOptions(input.options)
        acc.push(
          <Select
            label={`${input.label} ${
              input.isRequired ? '(Required)' : '(Optional)'
            }`}
            value={
              getValue(name, input.inputType, parentField) || options[0].value
            }
            onChange={onChange(name, parentField)}
            options={options}
          />,
        )
      }
      if (['button_group'].includes(input.inputType)) {
        const options = formatOptions(input.options)
        const value =
          getValue(name, input.inputType, parentField) || input.options[0].value

        let isGroupedWithNextInput = false
        if (input.inputGroup) {
          const [, nextInput] = arr[index + 1]
          isGroupedWithNextInput =
            input.inputGroup && input.inputGroup === nextInput.inputGroup
        }

        acc.push(
          <ToggleButtonUnstyled
            value={value}
            options={options}
            onChange={onChange(name, parentField)}
            className={isGroupedWithNextInput ? classes.buttonGroup : null}
          />,
        )
      }

      if (input.inputType === 'date') {
        acc.push(
          <DatePicker
            label={`${input.label} ${
              input.isRequired ? '(Required)' : '(Optional)'
            }`}
            value={getValue(name, input.inputType, parentField)}
            onChange={value =>
              onChange(name, parentField, null, true)(new Date(value))
            }
            description={input.description}
            error={input.error}
          />,
        )
      }

      if (input.inputType === 'form') {
        acc.push(<div className={classes.mt20} />)
        const parentField = name
        acc.push(...getInputs(input.form, parentField))
        acc.push(<div className={classes.mb20} />)
      }

      return acc
    }, [])
  }

  const handleClickSubmit = () => {
    setIsClickSubmit(true)
  }

  const onSubmit = async () => {
    setIsLoading(true)

    const form = getFormValues(fields)

    await IntegrationsService.createCoupons({
      integrationId,
      form,
    })

    const urlSearchParams = new URLSearchParams(history.location.search)
    const redirectURL = urlSearchParams.get('redirectURL')
    if (redirectURL) {
      return window.open(redirectURL, '_self')
    }
    const newUrl = `${PATHS.main.dashboard}${PATHS.dashboard.clientSettings}${
      PATHS.clientSettings.coupons
    }`

    history.push(newUrl)

    setIsLoading(false)
  }

  return (
    <div className={classname(sharedStyles.container, classes.wrap)}>
      <StyledBreadcrumbs
        icon={<BreadcrumbsSettingsIcon />}
        breadcrumbs={[
          {
            link: `${PATHS.main.dashboard}${PATHS.dashboard.clientSettings}`,
            label: 'Settings',
          },
          {
            link: `${PATHS.main.dashboard}${PATHS.dashboard.clientSettings}${
              PATHS.clientSettings.coupons
            }`,
            label: 'Coupons',
          },
          {
            link: `${PATHS.main.dashboard}${PATHS.dashboard.clientSettings}${
              PATHS.clientSettings.coupons
            }${PATHS.clientSettings.createCoupon}`,
            label: 'Create Coupon',
          },
          {
            link: `${PATHS.main.dashboard}${PATHS.dashboard.clientSettings}${
              PATHS.clientSettings.coupons
            }${PATHS.clientSettings.createCoupon}${
              PATHS.clientSettings.stores
            }`,
            label: 'Select Store',
          },
          {
            label: `Integration ${integrationId}`,
          },
        ]}
      />
      <div className={sharedStyles.titleWrap}>
        <PageTitle className={sharedStyles.title}>
          <div className={sharedStyles.back} onClick={onBack}>
            <ArrowBackIcon />
          </div>
          <div>Create coupon</div>
        </PageTitle>
      </div>

      <div className={classes.scroll}>
        {isLoading ? (
          <LoadingIndicator style={{ marginTop: '-50px' }} />
        ) : (
          <div className={classes.inputsWrapper}>
            {renderInputs}
            <Button
              disabled={isLoading}
              variant="outlined"
              onClick={handleClickSubmit}
              className={classname(sharedStyles.button, classes.button)}
            >
              Save
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

export default injectStyles(styles)(CouponForm)
